import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  Box,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Link,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableCaption,
  Flex,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { MdPerson, MdGavel, MdLocationOn, MdPhone, MdEmail, MdOpenInNew, MdSearch } from 'react-icons/md';
import { FaLinkedin } from 'react-icons/fa';

const AdvogadoDetailsModal = ({ isOpen, onClose, advogado, estado }) => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const highlightColor = useColorModeValue('blue.600', 'blue.300');

  useEffect(() => {
    if (isOpen && advogado && advogado.escavador_id) {
      fetchAdvogadoDetails();
    }
  }, [isOpen, advogado]);

  const fetchAdvogadoDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/precatorio${estado}/advogado/${advogado.escavador_id}`);
      if (!response.ok) {
        throw new Error('Falha ao buscar detalhes do advogado');
      }
      
      const data = await response.json();
      if (data.success) {
        setDetails(data.data);
      } else {
        throw new Error(data.message || 'Erro ao buscar detalhes');
      }
    } catch (err) {
      setError(err.message);
      console.error('Erro ao buscar detalhes do advogado:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const InfoField = ({ label, value, icon }) => (
    <HStack spacing={3} align="start">
      {icon && <Icon as={icon} color={highlightColor} boxSize={5} mt={0.5} />}
      <Box flex={1}>
        <Text fontSize="sm" color={textColor} fontWeight="medium">{label}</Text>
        <Text fontSize="md" mt={1}>{value || '-'}</Text>
      </Box>
    </HStack>
  );

  if (!advogado) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg={headerBg}>
          <Flex align="center" justify="space-between">
            <HStack spacing={3}>
              <Icon as={MdPerson} boxSize={6} color={highlightColor} />
              <Text>Detalhes do Advogado</Text>
            </HStack>
            {advogado.oabs && advogado.oabs.length > 0 && (
              <HStack spacing={2}>
                {advogado.oabs.slice(0, 3).map((oab, idx) => (
                  <Badge key={idx} colorScheme="teal" fontSize="xs">
                    OAB {oab.uf} {oab.numero}
                  </Badge>
                ))}
                {advogado.oabs.length > 3 && (
                  <Badge colorScheme="gray" fontSize="xs">
                    +{advogado.oabs.length - 3}
                  </Badge>
                )}
              </HStack>
            )}
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody py={6}>
          {loading ? (
            <VStack spacing={4} py={8}>
              <Spinner size="xl" color={highlightColor} thickness="4px" />
              <Text>Carregando detalhes do advogado...</Text>
            </VStack>
          ) : error ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <Text fontWeight="bold">Erro ao carregar detalhes</Text>
                <Text fontSize="sm">{error}</Text>
              </Box>
            </Alert>
          ) : (
            <VStack spacing={6} align="stretch">
              {/* Basic Info Card */}
              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardHeader pb={3}>
                  <Heading size="md">{advogado.nome}</Heading>
                  {advogado.tipo_normalizado && (
                    <Badge colorScheme="purple" mt={2}>{advogado.tipo_normalizado}</Badge>
                  )}
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <InfoField 
                      label="Tipo de Pessoa" 
                      value={advogado.tipo_pessoa} 
                      icon={MdPerson}
                    />
                    <InfoField 
                      label="Quantidade de Processos" 
                      value={advogado.quantidade_processos} 
                      icon={MdGavel}
                    />
                    {advogado.cpf && (
                      <InfoField 
                        label="CPF" 
                        value={advogado.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')} 
                      />
                    )}
                    {advogado.cnpj && (
                      <InfoField 
                        label="CNPJ" 
                        value={advogado.cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')} 
                      />
                    )}
                  </SimpleGrid>

                  {/* OAB Numbers */}
                  {advogado.oabs && advogado.oabs.length > 0 && (
                    <Box mt={6}>
                      <Text fontWeight="bold" mb={3}>Inscrições OAB</Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        {advogado.oabs.map((oab, idx) => (
                          <HStack key={idx} p={3} bg={headerBg} borderRadius="md" justify="space-between">
                            <HStack>
                              <Badge colorScheme="teal">{oab.uf}</Badge>
                              <Text fontWeight="medium">{oab.numero}</Text>
                            </HStack>
                            <Badge variant="subtle" colorScheme={oab.tipo === 'PRINCIPAL' ? 'green' : 'gray'}>
                              {oab.tipo}
                            </Badge>
                          </HStack>
                        ))}
                      </SimpleGrid>
                    </Box>
                  )}
                </CardBody>
              </Card>

              {/* Detailed Information from API */}
              {details && (
                <Tabs colorScheme="blue" variant="enclosed">
                  <TabList>
                    <Tab>Informações Gerais</Tab>
                    {details.enderecos && details.enderecos.length > 0 && <Tab>Endereços</Tab>}
                    {details.processos_recentes && details.processos_recentes.length > 0 && <Tab>Processos Recentes</Tab>}
                    {details.estatisticas && <Tab>Estatísticas</Tab>}
                  </TabList>

                  <TabPanels>
                    {/* General Information Tab */}
                    <TabPanel>
                      <VStack spacing={4} align="stretch">
                        {details.informacoes_pessoais && (
                          <Card variant="outline" size="sm">
                            <CardHeader>
                              <Heading size="sm">Informações Pessoais</Heading>
                            </CardHeader>
                            <CardBody>
                              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                {details.informacoes_pessoais.nome_completo && (
                                  <InfoField label="Nome Completo" value={details.informacoes_pessoais.nome_completo} />
                                )}
                                {details.informacoes_pessoais.data_nascimento && (
                                  <InfoField label="Data de Nascimento" value={formatDate(details.informacoes_pessoais.data_nascimento)} />
                                )}
                                {details.informacoes_pessoais.nacionalidade && (
                                  <InfoField label="Nacionalidade" value={details.informacoes_pessoais.nacionalidade} />
                                )}
                                {details.informacoes_pessoais.estado_civil && (
                                  <InfoField label="Estado Civil" value={details.informacoes_pessoais.estado_civil} />
                                )}
                              </SimpleGrid>
                            </CardBody>
                          </Card>
                        )}

                        {details.contatos && (
                          <Card variant="outline" size="sm">
                            <CardHeader>
                              <Heading size="sm">Contatos</Heading>
                            </CardHeader>
                            <CardBody>
                              <VStack spacing={3} align="stretch">
                                {details.contatos.emails && details.contatos.emails.length > 0 && (
                                  <Box>
                                    <HStack spacing={2} mb={2}>
                                      <Icon as={MdEmail} color={highlightColor} />
                                      <Text fontWeight="medium">E-mails:</Text>
                                    </HStack>
                                    {details.contatos.emails.map((email, idx) => (
                                      <Link key={idx} href={`mailto:${email}`} color={highlightColor} fontSize="sm" display="block" ml={7}>
                                        {email}
                                      </Link>
                                    ))}
                                  </Box>
                                )}
                                {details.contatos.telefones && details.contatos.telefones.length > 0 && (
                                  <Box>
                                    <HStack spacing={2} mb={2}>
                                      <Icon as={MdPhone} color={highlightColor} />
                                      <Text fontWeight="medium">Telefones:</Text>
                                    </HStack>
                                    {details.contatos.telefones.map((tel, idx) => (
                                      <Text key={idx} fontSize="sm" ml={7}>{tel}</Text>
                                    ))}
                                  </Box>
                                )}
                                {details.contatos.linkedin && (
                                  <Box>
                                    <HStack spacing={2}>
                                      <Icon as={FaLinkedin} color={highlightColor} />
                                      <Link href={details.contatos.linkedin} isExternal color={highlightColor}>
                                        Perfil LinkedIn <Icon as={MdOpenInNew} mx="2px" />
                                      </Link>
                                    </HStack>
                                  </Box>
                                )}
                              </VStack>
                            </CardBody>
                          </Card>
                        )}
                      </VStack>
                    </TabPanel>

                    {/* Addresses Tab */}
                    {details.enderecos && details.enderecos.length > 0 && (
                      <TabPanel>
                        <VStack spacing={4} align="stretch">
                          {details.enderecos.map((endereco, idx) => (
                            <Card key={idx} variant="outline" size="sm">
                              <CardBody>
                                <HStack spacing={2} mb={3}>
                                  <Icon as={MdLocationOn} color={highlightColor} boxSize={5} />
                                  <Text fontWeight="bold">{endereco.tipo || 'Endereço'}</Text>
                                  {endereco.principal && (
                                    <Badge colorScheme="green" size="sm">Principal</Badge>
                                  )}
                                </HStack>
                                <Text>{endereco.logradouro}, {endereco.numero}</Text>
                                {endereco.complemento && <Text fontSize="sm">{endereco.complemento}</Text>}
                                <Text>{endereco.bairro}</Text>
                                <Text>{endereco.cidade} - {endereco.estado}</Text>
                                <Text>CEP: {endereco.cep}</Text>
                              </CardBody>
                            </Card>
                          ))}
                        </VStack>
                      </TabPanel>
                    )}

                    {/* Recent Cases Tab */}
                    {details.processos_recentes && details.processos_recentes.length > 0 && (
                      <TabPanel>
                        <Table variant="simple" size="sm">
                          <TableCaption>Últimos processos do advogado</TableCaption>
                          <Thead>
                            <Tr>
                              <Th>Número</Th>
                              <Th>Tribunal</Th>
                              <Th>Data</Th>
                              <Th>Status</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {details.processos_recentes.map((proc, idx) => (
                              <Tr key={idx}>
                                <Td>
                                  <Link color={highlightColor} href={proc.link} isExternal>
                                    {proc.numero} <Icon as={MdOpenInNew} mx="2px" />
                                  </Link>
                                </Td>
                                <Td>{proc.tribunal}</Td>
                                <Td>{formatDate(proc.data_distribuicao)}</Td>
                                <Td>
                                  <Badge colorScheme={proc.ativo ? 'green' : 'gray'}>
                                    {proc.ativo ? 'Ativo' : 'Arquivado'}
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TabPanel>
                    )}

                    {/* Statistics Tab */}
                    {details.estatisticas && (
                      <TabPanel>
                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                          <Stat>
                            <StatLabel>Total de Processos</StatLabel>
                            <StatNumber>{details.estatisticas.total_processos || 0}</StatNumber>
                            <StatHelpText>Em todos os tribunais</StatHelpText>
                          </Stat>
                          <Stat>
                            <StatLabel>Processos Ativos</StatLabel>
                            <StatNumber color="green.500">{details.estatisticas.processos_ativos || 0}</StatNumber>
                            <StatHelpText>Em andamento</StatHelpText>
                          </Stat>
                          <Stat>
                            <StatLabel>Processos Arquivados</StatLabel>
                            <StatNumber color="gray.500">{details.estatisticas.processos_arquivados || 0}</StatNumber>
                            <StatHelpText>Finalizados</StatHelpText>
                          </Stat>
                        </SimpleGrid>

                        {details.estatisticas.tribunais_mais_atuados && (
                          <Box mt={6}>
                            <Heading size="sm" mb={3}>Tribunais Mais Atuados</Heading>
                            <VStack spacing={2} align="stretch">
                              {details.estatisticas.tribunais_mais_atuados.map((tribunal, idx) => (
                                <HStack key={idx} justify="space-between" p={2} bg={headerBg} borderRadius="md">
                                  <Text>{tribunal.nome}</Text>
                                  <Badge>{tribunal.quantidade} processos</Badge>
                                </HStack>
                              ))}
                            </VStack>
                          </Box>
                        )}
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              )}

              {/* Escavador Link */}
              {advogado.escavador_id && (
                <Box textAlign="center" pt={4}>
                  <Link
                    href={`https://www.escavador.com/pessoas/${advogado.escavador_id}`}
                    isExternal
                    color={highlightColor}
                  >
                    <HStack spacing={2} justify="center">
                      <Icon as={MdSearch} />
                      <Text>Ver perfil completo no Escavador</Text>
                      <Icon as={MdOpenInNew} />
                    </HStack>
                  </Link>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
        
        <ModalFooter>
          <Button onClick={onClose}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdvogadoDetailsModal;